import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Card, TextInput, Button, Text, Alert, Stack, PinInput,
} from '@mantine/core'
import { IconAlertCircle, IconMail, IconShieldOff } from '@tabler/icons-react'
import { authApi } from '@/api/auth'
import { BrandLogo } from '@/components/ui/BrandLogo'

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
    '&:focus': { borderColor: 'var(--ai-brand-verde)' },
  },
}

type NextStep = 'enter_code' | 'check_whatsapp' | 'contact_admin' | 'check_email' | 'send_failed'

export function ForgotTotp() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null)
  const [nextStep, setNextStep] = useState<NextStep | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) { setError('Informe seu e-mail.'); return }
    setError(''); setInfo(''); setLoading(true)
    try {
      const res = await authApi.forgotTotp(email.trim().toLowerCase())
      if (res.next_step === 'send_failed') {
        // Nao seta maskedPhone de proposito: o formulario continua na tela,
        // com o erro acima dele, pra pessoa poder tentar de novo. Setar
        // esconderia o unico botao e deixaria a tela sem saida.
        setError(
          'Não conseguimos enviar o código para o seu WhatsApp agora — o '
          + 'problema e nosso, não seu. Tente de novo em alguns minutos. Se '
          + 'continuar, procure um administrador do sistema.',
        )
        return
      }
      setMaskedPhone(res.masked_phone)
      setNextStep(res.next_step ?? 'enter_code')
      if (res.next_step === 'check_email') {
        setInfo(
          'Sua conta não tem telefone cadastrado. Enviamos para o seu e-mail '
          + 'um link seguro pra cadastrar telefone (expira em 30 minutos). '
          + 'Após cadastrar, volte aqui pra solicitar o código de reset.',
        )
      } else if (res.next_step === 'contact_admin') {
        setError(
          'Não foi possível dar sequência na sua solicitação. '
          + 'Procure um administrador do sistema.',
        )
      } else if (res.next_step === 'check_whatsapp') {
        setInfo(
          'Se houver uma conta com esse e-mail, enviamos uma mensagem para o '
          + 'WhatsApp cadastrado no sistema com os próximos passos. Verifique '
          + 'seu celular. Pode demorar até 1 minuto.',
        )
      } else {
        setInfo(`Enviamos um código de 6 dígitos para ${res.masked_phone} via WhatsApp. Pode demorar até 1 minuto.`)
      }
    } catch {
      setError('Erro ao solicitar reset. Tente novamente em instantes.')
    } finally {
      setLoading(false)
    }
  }

  const triggerVerify = async (raw: string) => {
    if (raw.length !== 6) return
    setError(''); setLoading(true)
    try {
      await authApi.verifyForgotTotp(email.trim().toLowerCase(), raw)
      navigate('/login', { replace: true })
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (detail === 'too_many_attempts') {
        setError('Muitas tentativas erradas. Solicite um novo código.')
        setMaskedPhone(null); setCode('')
      } else if (detail === 'kanidm_unavailable' || detail === 'kanidm_remove_failed') {
        setError('Serviço indisponível. Tente novamente em instantes.')
        setCode('')
      } else {
        setError('Código inválido ou expirado.')
        setCode('')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(9, 141, 147, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text size="xs" c="rgba(255,255,255,0.35)" mt={8} fw={600} tt="uppercase" style={{ letterSpacing: '0.08em' }}>
            Resetar 2FA / TOTP
          </Text>
        </Stack>

        <Card
          radius="md"
          p="xl"
          style={{
            backgroundColor: 'var(--ai-auth-card-bg)',
            border: '1px solid var(--ai-auth-border)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <IconShieldOff size={22} color="#098d93" />
            <Text fw={700} size="lg" c="#e9ecef">Resetar autenticador</Text>
          </Box>
          <Text size="xs" c="#6c757d" mb="lg">
            Enviaremos um código de 6 dígitos via WhatsApp para o telefone cadastrado.
            Após confirmar, você podera reconfigurar seu app autenticador.
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" radius="sm"
              styles={{ root: { backgroundColor: 'rgba(255, 91, 87, 0.1)', border: '1px solid rgba(255, 91, 87, 0.2)' } }}
            >
              {error}
            </Alert>
          )}
          {info && !error && (
            <Alert color="teal" mb="md" radius="sm"
              styles={{ root: { backgroundColor: 'rgba(9, 141, 147, 0.1)', border: '1px solid rgba(9, 141, 147, 0.2)' } }}
            >
              {info}
            </Alert>
          )}

          {!maskedPhone && (
            <form onSubmit={handleRequest}>
              <Stack gap="sm">
                <TextInput
                  label="E-mail"
                  placeholder="seu@email.com.br"
                  leftSection={<IconMail size={16} />}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  autoComplete="email"
                  required
                  size="md"
                  styles={inputStyles}
                />
                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  mt={8}
                  size="md"
                  radius="sm"
                  style={{
                    backgroundColor: 'var(--ai-brand-verde)',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Enviar código
                </Button>
              </Stack>
            </form>
          )}

          {maskedPhone && nextStep === 'contact_admin' && (
            <Stack gap="md" align="center">
              <Button
                type="button"
                fullWidth
                size="md"
                radius="sm"
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: 'var(--ai-brand-verde)',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                Voltar ao login
              </Button>
              <Button
                type="button"
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => {
                  setMaskedPhone(null); setNextStep(null)
                  setCode(''); setInfo(''); setError('')
                }}
              >
                Tentar com outro e-mail
              </Button>
            </Stack>
          )}

          {nextStep === 'check_email' && (
            <Stack gap="md" align="center">
              <Button
                type="button"
                fullWidth
                size="md"
                radius="sm"
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: 'var(--ai-brand-verde)',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                Voltar ao login
              </Button>
              <Button
                type="button"
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => {
                  setMaskedPhone(null); setNextStep(null)
                  setCode(''); setInfo(''); setError('')
                }}
              >
                Tentar com outro e-mail
              </Button>
            </Stack>
          )}

          {maskedPhone && nextStep === 'check_whatsapp' && (
            <Stack gap="md" align="center">
              <Button
                type="button"
                fullWidth
                size="md"
                radius="sm"
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: 'var(--ai-brand-verde)',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                Voltar ao login
              </Button>
              <Button
                type="button"
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => {
                  setMaskedPhone(null); setNextStep(null)
                  setCode(''); setInfo(''); setError('')
                }}
              >
                Tentar com outro e-mail
              </Button>
            </Stack>
          )}

          {maskedPhone && nextStep === 'enter_code' && (
            <Stack gap="md" align="center">
              <Text size="sm" c="#b6c2c9">Digite o código de 6 dígitos:</Text>
              <PinInput
                length={6}
                type="number"
                value={code}
                onChange={setCode}
                autoFocus
                disabled={loading}
                onComplete={(value) => { setCode(value); void triggerVerify(value) }}
                styles={{
                  input: {
                    backgroundColor: 'var(--ai-auth-input-bg)',
                    borderColor: 'var(--ai-auth-border)',
                    color: '#e9ecef',
                  },
                }}
              />
              <Button
                type="button"
                fullWidth
                size="md"
                radius="sm"
                disabled={code.length !== 6}
                loading={loading}
                onClick={() => void triggerVerify(code)}
                style={{
                  backgroundColor: code.length === 6 ? 'var(--ai-brand-verde)' : undefined,
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                Confirmar
              </Button>
              <Button
                type="button"
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => {
                  setMaskedPhone(null); setNextStep(null)
                  setCode(''); setInfo(''); setError('')
                }}
              >
                Solicitar novo código
              </Button>
            </Stack>
          )}

          <Text
            size="xs" c="#098d93" ta="center" mt="lg" fw={600}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/login')}
          >
            Voltar ao login
          </Text>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
